import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { Tooltip } from 'antd';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import './Wellness.less';
import WellnessIconByTitle from './WellnessIconByTitle';

const WellnessListItem = ({
  showViewHistoryModal,
  title,
  day,
  timeDifference,
  time,
  data,
  device,
  deviceBrand,
  deviceModel,
  deviceuuid,
  showViewHistory = false,
  eventName,
  hasData,
  getTranslatedLabel,
  isSelected = false,
  formattedDate,
}) => {
  const trans = useSelector(translator);

  const truncateText = (text) => {
    if (!text) return;
    return text?.length <= 15 ? text : text?.substring(0, 15) + '...';
  };

  const isEmpty = !Array.isArray(data) || data.length === 0 || hasData === false;

  const roundToOneDecimal = (value) => {
    const numericValue = parseFloat(value);
    return !isNaN(numericValue) ? Math.ceil(numericValue * 10) / 10 : value;
  };

  const getTranslatedDay = (dayString) => {
    // Handle Today and Yesterday labels from getDayLabel
    if (dayString === 'Today' || dayString === 'TODAY') {
      return trans('TODAY');
    }
    if (dayString === 'Yesterday' || dayString === 'YESTERDAY') {
      return trans('YESTERDAY');
    }
    // For other day names (like "Monday", "Tuesday", etc.), translate them
    const dayName = dayString.toUpperCase();
    return trans(dayName);
  };

  const handleCardClick = () => {
    if (!isEmpty && showViewHistoryModal) {
      showViewHistoryModal(title, deviceBrand, deviceModel, eventName);
    }
  };

  // Determine what to display: day label or formatted date
  const displayDay =
    day === 'Today' || day === 'Yesterday' ? getTranslatedDay(day) : formattedDate || day;

  return (
    <>
      <div
        className={`wellness-card-container ${isSelected ? 'wellness-card-selected' : ''}`}
        onClick={handleCardClick}
      >
        <div className="wellness-time-container">
          <p className="wellness-time-title">{displayDay}</p>
          <p className="wellness-time-value">{time}</p>
        </div>
        <div className="wellness-device-info-container">
          <div className="wellness-device-icon">
            <WellnessIconByTitle title={title} style={{ width: '55px', height: '55px' }} />
          </div>
          <div className="wellness-device-heading">
            <Tooltip placement="bottom" title={deviceBrand?.length > 15 ? deviceBrand : ''}>
              <div className="wellness-device-brand">{truncateText(deviceBrand)}</div>
            </Tooltip>
            {deviceModel && (
              <Tooltip placement="bottom" title={deviceModel?.length > 15 ? deviceModel : ''}>
                <div className="wellness-device-model">{truncateText(deviceModel)}</div>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="wellness-content-container">
          {(() => {
            const dataArray = data || [];
            const result = [];
            let pulseReading = null;
            const primaryReadings = [];

            // Separate pulse readings from primary readings
            dataArray.forEach((item) => {
              const label = item.display_label?.toLowerCase() || '';
              if (label.includes('pulse')) {
                pulseReading = item;
              } else {
                primaryReadings.push(item);
              }
            });

            // Render primary readings container
            if (primaryReadings.length > 0) {
              const primaryItems = [];
              let i = 0;

              while (i < primaryReadings.length) {
                const item = primaryReadings[i];
                const label = item.display_label?.toLowerCase() || '';

                // Check if this is systolic and next is diastolic for blood pressure grouping
                if (label.includes('systolic') && i + 1 < primaryReadings.length) {
                  const nextItem = primaryReadings[i + 1];
                  const nextLabel = nextItem.display_label?.toLowerCase() || '';

                  if (nextLabel.includes('diastolic')) {
                    // Group systolic and diastolic together
                    primaryItems.push(
                      <div className="wellness-data-item" key={i}>
                        <div className="wellness-data-main">
                          <div className="wellness-data-label">
                            {getTranslatedLabel(item.display_label)}
                          </div>
                          <div className="wellness-data-value-container">
                            <div className="wellness-data-value">
                              {roundToOneDecimal(item.display_value)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                    primaryItems.push(
                      <div className="wellness-data-item" key={i + 1}>
                        <div className="wellness-data-main">
                          <div className="wellness-data-label">
                            {getTranslatedLabel(nextItem.display_label)}
                          </div>
                          <div className="wellness-data-value-container">
                            <div className="wellness-data-value">
                              {roundToOneDecimal(nextItem.display_value)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                    i += 2; // Skip both items
                    continue;
                  }
                }

                // Regular single item
                primaryItems.push(
                  <div className="wellness-data-item" key={i}>
                    <div className="wellness-data-main">
                      <div className="wellness-data-label">
                        {getTranslatedLabel(item.display_label)}
                      </div>
                      <div className="wellness-data-value-container">
                        <div className="wellness-data-value">
                          {roundToOneDecimal(item.display_value)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
                i++;
              }

              result.push(
                <div className="wellness-primary-readings" key="primary">
                  {primaryItems}
                </div>
              );
            }

            // Render pulse reading container (always in same position)
            if (pulseReading) {
              result.push(
                <div className="wellness-pulse-reading" key="pulse">
                  <div className="wellness-data-item">
                    <div className="wellness-data-main">
                      <div className="wellness-data-label">
                        {getTranslatedLabel(pulseReading.display_label)}
                      </div>
                      <div className="wellness-data-value-container">
                        <div className="wellness-data-value">
                          {roundToOneDecimal(pulseReading.display_value)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            return result;
          })()}
        </div>
      </div>
    </>
  );
};

WellnessListItem.propTypes = {
  showViewHistoryModal: PropTypes.func,
  title: PropTypes.string,
  day: PropTypes.string,
  timeDifference: PropTypes.string,
  time: PropTypes.string,
  data: PropTypes.array,
  device: PropTypes.string,
  deviceBrand: PropTypes.string,
  deviceModel: PropTypes.string,
  deviceuuid: PropTypes.string,
  showViewHistory: PropTypes.bool,
  eventName: PropTypes.string,
  isSelected: PropTypes.bool,
  formattedDate: PropTypes.string,
};

export default WellnessListItem;
